// src/hooks/useDragDropZone.ts

import { useState, useCallback, useRef } from "react";
import { createLogger } from "../utils/logger";

const log = createLogger("useDragDropZone");

/**
 * Props for the useDragDropZone hook
 */
interface UseDragDropZoneProps {
  onFilesDropped: (files: File[]) => void;
  isEnabled?: boolean;
}

/**
 * Return type for the useDragDropZone hook
 */
interface UseDragDropZoneReturn {
  isDragging: boolean;
  isSupported: boolean;
  manualClose: () => void;
  dragHandlers: {
    onDragEnter?: (e: React.DragEvent) => void;
    onDragLeave?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
  };
}

/**
 * Custom hook that makes an element a drag-and-drop zone for files
 *
 * Features:
 * - Simple feature detection (no platform-specific code)
 * - Handles nested element drag events properly with counter
 * - Provides visual feedback state (isDragging)
 * - Graceful degradation if drag-drop not supported
 *
 * @param onFilesDropped - Callback when files are dropped
 * @param isEnabled - Whether drag-drop is enabled (default: true)
 * @returns Drag state and event handlers to spread on the container
 */
export function useDragDropZone({
  onFilesDropped,
  isEnabled = true,
}: UseDragDropZoneProps): UseDragDropZoneReturn {
  const [isDragging, setIsDragging] = useState(false);

  // Use a counter to handle nested elements properly
  // This prevents the overlay from flickering when dragging over child elements
  const dragCounter = useRef(0);

  // Simple feature detection - check if drag-drop APIs exist
  const isDragDropAvailable =
    isEnabled &&
    typeof document !== "undefined" &&
    "draggable" in document.createElement("div") &&
    "ondrop" in document.createElement("div");

  // Handle drag enter - user starts dragging over our container
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current++;

    // Only show overlay if we're dragging files
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      log.debug("Drag enter detected", {
        counter: dragCounter.current,
        itemCount: e.dataTransfer.items.length,
      });
      setIsDragging(true);
    }
  }, []);

  // Handle drag leave - user stops dragging over our container
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current--;

    // Only hide overlay when we've left all nested elements
    if (dragCounter.current === 0) {
      log.debug("Drag leave - hiding overlay", { counter: dragCounter.current });
      setIsDragging(false);
    }
  }, []);

  // Handle drag over - required to allow drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Set the drop effect to copy (shows + icon)
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  // Handle drop - user drops files
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset state
      setIsDragging(false);
      dragCounter.current = 0;

      // Extract files from the drop event
      const files = Array.from(e.dataTransfer?.files || []);

      if (files.length > 0) {
        log.info("Files dropped", {
          count: files.length,
          fileNames: files.map((f) => f.name),
          fileSizes: files.map((f) => f.size),
        });

        // Call the callback with the dropped files
        onFilesDropped(files);
      } else {
        log.warn("Drop event with no files");
      }
    },
    [onFilesDropped]
  );

  // Manual close - user clicks close button on overlay
  const manualClose = useCallback(() => {
    log.debug("Manual close triggered");
    setIsDragging(false);
    dragCounter.current = 0;
  }, []);

  // Return the hook interface
  return {
    isDragging,
    isSupported: isDragDropAvailable,
    manualClose,
    dragHandlers: isDragDropAvailable
      ? {
          onDragEnter: handleDragEnter,
          onDragLeave: handleDragLeave,
          onDragOver: handleDragOver,
          onDrop: handleDrop,
        }
      : {},
  };
}
